import Queue from 'promise-queue';
import * as webdriverio from 'webdriverio';
import * as config from '../commons/config';
import * as perf from '../commons/performance-logger';
import { extractElementId } from '../utils';
import { getLogger } from '../commons/logger';
import { W3C_ELEMENT_ID } from './constants';
import { isElementDisplayed } from './scripts/isElementDisplayed';
import { isOldProtocol, encodeForSafari } from './webDriverUtils';
import { SELENIUM_PERF_MARKS } from '../commons/SeleniumPerfStats';
import type { LegacyElement, W3CElement } from '../utils';
import type { SeleniumPerfStats } from '../commons/SeleniumPerfStats';
import type { ActionOffsets } from './webdriver';
import type { Capabilities } from '@wdio/types';

type RemoteOptions = Capabilities.RemoteConfig;

const logger = getLogger('WebDriverApi');

const getViewportSizeHelper = function () {
    return {
        screenWidth: Math.floor(window.innerWidth || 0),
        screenHeight: Math.floor(window.innerHeight || 0),
    };
};

const timeoutParamIndex = {
    implicit: 0, pageLoad: 1, script: 2,
};

export class WebdriverioWebDriverApi {
    seleniumPerfStats?: SeleniumPerfStats;
    client?: WebdriverIO.Browser;
    queue!: Queue;
    private testName?: string;
    protected testResultId?: string;
    protected readonly unsupportedActions = new Set<'execute' | 'executeAsync' | 'elementIdDisplayed' | 'timeouts' | 'keys' | 'windowHandleSize' | 'move' | 'submit'>();

    get capabilities() {
        return this.client?.capabilities;
    }

    get browserName() {
        return (this.capabilities && 'browserName' in this.capabilities && this.capabilities.browserName?.toLocaleLowerCase()) || undefined;
    }

    windowHandleMaximize() {
        return this.addToQueue(() => this.client?.maximizeWindow());
    }

    rejectWithLog(err: any, func?: () => any): Promise<never> {
        const { testName, testResultId } = this;
        const crashingFunc = func ? func.toString().slice(0, 2000) : '';
        logger.warn('error from selenium', {
            err, testResultId, testName, crashingFunc,
        });
        throw err;
    }

    initQueueRequests() {
        let maxConcurrent = Infinity;
        if (this.isAndroid()) {
            maxConcurrent = 1;
        }
        if (config.REQUESTS_QUEUE_SIZE !== undefined) {
            maxConcurrent = config.REQUESTS_QUEUE_SIZE;
        }
        const maxQueue = Infinity;
        this.queue = new Queue(maxConcurrent, maxQueue);
    }

    async addToQueue<T>(func: () => undefined | Promise<T>) {
        const perfId = this.seleniumPerfStats?.markStart();
        try {
            return await this.queue.add(func as () => Promise<T>);
        } catch (err) {
            return this.rejectWithLog(err, func);
        } finally {
            this.seleniumPerfStats?.markEnd(perfId!);
        }
    }

    async installExtensions(extensionsToUploadWithBidi?: { unzippedTestimExtensionPath?: string, unzippedCustomExtensionPath?: string }) {
        // Upload extensions (Testim extension and custom extension) to the browser using the WebExtension API
        const extensionPaths = [
            extensionsToUploadWithBidi?.unzippedTestimExtensionPath,
            extensionsToUploadWithBidi?.unzippedCustomExtensionPath
        ].filter(Boolean);

        for (const extensionPath of extensionPaths) {
            await this.client?.send({
                method: 'webExtension.install',
                params: {
                    extensionData: {
                        type: 'path',
                        path: extensionPath
                    }
                },
            });
        }
    }

    async initClient(capabilities: RemoteOptions, testName?: string, testResultId?: string, useLocalChromeDriver?: boolean, extensionsToUploadWithBidi?: { unzippedTestimExtensionPath?: string, unzippedCustomExtensionPath?: string }) {
        this.testName = testName;
        this.testResultId = testResultId;
        // silence warning regarding `buttonPress` being deprecated
        // @ts-expect-error Not sure if this is relevant with wdio7
        capabilities.deprecationWarnings = false;
        this.initQueueRequests();
        perf.log('right before addToQueue');
        const extUrl = (capabilities.capabilities as any).alwaysMatch?.loadExtension ||
            (capabilities.capabilities as any).alwaysMatch?.loadExt ||
            (capabilities.capabilities as any).alwaysMatch?.['goog:chromeOptions']?.extensions?.[0]?.slice(0, 80);
        const perfId = this.seleniumPerfStats?.markStart(SELENIUM_PERF_MARKS.GET_BROWSER);
        try {
            await this.addToQueue(async () => {
                logger.info('requesting browser', { testResultId, testName, extUrl });
                perf.log('before this.client.init');
                // add test-result-id to headers, used by EKS Grid router to route request to the browser based on test-result-id
                capabilities.headers ||= {};
                capabilities.headers['Test-Result-Id'] = testResultId ?? '';
                this.client = await webdriverio.remote(capabilities);

                // When using BiDi, the DialogManager auto-subscribes to dialog events and tries to handle them
                // However if we already dismissed the dialog with acceptAlert, it can cause "no such alert" errors.
                // By registering our own listener, we take control of dialog handling and can gracefully handle this.
                // Note - type 'dialog' is not fully typed in WebDriverIO, so we use any
                (this.client as any).on('dialog', async (dialog: { accept: () => Promise<void> }) => {
                    try {
                        await dialog.accept();
                    } catch {
                        // Ignore as the dialog was already dismissed.
                    }
                });

                if (useLocalChromeDriver) {
                    await this.installExtensions(extensionsToUploadWithBidi);
                }
                this.logGridStatus(testResultId);
            });
            perf.log('after client init');
        } finally {
            this.seleniumPerfStats?.markEnd(perfId!, SELENIUM_PERF_MARKS.GET_BROWSER);
        }
    }

    private async logGridStatus(testResultId?: string) {
        if (!this.client) {
            return;
        }
        const { sessionId } = this.client;
        let browserNode: unknown = 'N/A';
        let gridSlot: unknown = 'N/A';
        let status: any;
        try {
            status = await this.client.status();
            for (const nodes of status.nodes || []) {
                for (const slot of nodes.slots || []) {
                    if (slot?.session?.sessionId === sessionId) {
                        browserNode = nodes;
                        gridSlot = slot;
                        break;
                    }
                }
            }
        } catch (error) {
            if (error.message === 'unknown error') {
                logger.info('grid status is not supported by the grid', { testResultId });
                return;
            }
            logger.error('failed to get grid status', { error, testResultId });
            return;
        }
        if (browserNode && typeof browserNode === 'object' && 'slots' in browserNode) {
            delete browserNode?.slots; // there can be lots of slots, we don't want to log them
        }
        logger.info('grid status', { gridStatusMsg: status.message, ready: status.ready, browserNode, gridSlot, testResultId });
    }

    get isMobile() {
        return this.client?.isMobile;
    }

    getSessionId() {
        return this.client?.sessionId;
    }

    isChrome() {
        return this.browserName === 'chrome';
    }

    isChromium() {
        return this.isChrome() || this.isEdgeChromium();
    }

    isFirefox() {
        return this.browserName === 'firefox';
    }

    isSafari() {
        return this.browserName === 'safari' || this.browserName === 'safari technology preview';
    }

    isAndroid() {
        return this.capabilities && 'platformName' in this.capabilities && this.capabilities.platformName?.toLocaleLowerCase() === 'android';
    }

    isEdgeChromium() {
        const isOldEdge = this.capabilities && '_isOldEdge' in this.capabilities && this.capabilities._isOldEdge;
        return this.browserName === 'microsoftedge' && !isOldEdge;
    }

    async execute<T extends (...args: any[]) => any = any>(script: T | string, ...args: Parameters<T>): Promise<{ value: Awaited<ReturnType<T>> }> {
        const value = await this.addToQueue(async () => {
            /* parameter check */
            if ((typeof script !== 'string' && typeof script !== 'function')) {
                throw new TypeError('number or type of arguments don\'t agree with execute protocol command');
            }

            /* instances started as `multibrowserinstance` can't getting called with a function parameter, therefor we need to check if it starts with "function () {" */
            if (typeof script === 'function') {
                script = `return (${script}).apply(null, arguments)`;
            }

            const decorateErrorWithExecutedScript = (err: any) => {
                Object.assign(err, { executedScript: script });
                throw err;
            };

            const newEndpoint = () => this.client!.execute(script, args).catch(decorateErrorWithExecutedScript);
            const oldEndpoint = () => this.client!.executeScript(script as string, args).catch(decorateErrorWithExecutedScript);

            if (this.unsupportedActions.has('execute')) {
                return newEndpoint();
            }

            args = args.map(arg => {
                if (arg === null) {
                    logger.warn('The function "executeScript" in webdriverio can no longer accept "null" as an argument, it was replaced with "undefined"', { script, args });
                    return undefined;
                }
                return arg;
            }) as Parameters<T>;

            try {
                return await oldEndpoint();
            } catch (err) {
                /** jsonwire command not supported try webdriver endpoint */
                if (isOldProtocol(err)) {
                    this.unsupportedActions.add('execute');
                    return newEndpoint();
                }

                throw err;
            }
        });
        return { value };
    }

    /**
     * @param script - a script that must accept a callback, and call it when they finish running.
     * @param args - the script arguments.
     */
    async executeAsync<T, TArgs extends any[]>(script: string | ((...args: [...innerArgs: TArgs, callback: (result?: T) => void]) => void), ...args: TArgs): Promise<{ value: T }> {
        const value = await this.addToQueue(async () => {
            /* parameter check */
            if ((typeof script !== 'string' && typeof script !== 'function')) {
                throw new TypeError('number or type of arguments don\'t agree with execute protocol command');
            }

            /* instances started as `multibrowserinstance` can't getting called with a function parameter, therefor we need to check if it starts with "function () {" */
            if (typeof script === 'function') {
                script = `return (${script}).apply(null, arguments)`;
            }

            const newEndpoint = () => this.client!.execute(script as string, ...args);
            const oldEndpoint = () => this.client!.executeAsyncScript(script as string, args);

            if (this.unsupportedActions.has('executeAsync')) {
                return newEndpoint();
            }

            try {
                return await oldEndpoint();
            } catch (err) {
                /** jsonwire command not supported try webdriver endpoint */
                if (isOldProtocol(err)) {
                    this.unsupportedActions.add('executeAsync');
                    return newEndpoint();
                }

                throw err;
            }
        });
        return { value };
    }

    async executeCDP(command: string, parameters: object | undefined = {}) {
        if (!this.isChromium()) {
            return undefined;
        }
        const result = await this.client?.sendCommandAndGetResult(command, parameters);
        if (!result?.targetInfos) {
            return [];
        }
        return result.targetInfos;
    }

    async takeScreenshot() {
        const perfId = this.seleniumPerfStats?.markStart(SELENIUM_PERF_MARKS.GET_SCREENSHOT);
        try {
            const value = await this.addToQueue(() => this.client?.takeScreenshot());
            return { value };
        } finally {
            this.seleniumPerfStats?.markEnd(perfId!, SELENIUM_PERF_MARKS.GET_SCREENSHOT);
        }
    }

    async takeElementScreenshot(element: W3CElement | LegacyElement) {
        const perfId = this.seleniumPerfStats!.markStart(SELENIUM_PERF_MARKS.GET_SCREENSHOT);
        try {
            return await this.addToQueue(() => this.client!.takeElementScreenshot(extractElementId(element)));
        } finally {
            this.seleniumPerfStats!.markEnd(perfId, SELENIUM_PERF_MARKS.GET_SCREENSHOT);
        }
    }

    async getElementBySelector(selector: string) {
        const value = await this.addToQueue(async () => this.client?.$(selector));
        return { value };
    }

    elementIdDisplayed(elementId: string) {
        return this.addToQueue(async () => {
            const oldEndpoint = () => this.client!.isElementDisplayed(elementId).then(value => ({ value }));
            const newEndpoint = () => this.execute(isElementDisplayed, { ELEMENT: elementId, [W3C_ELEMENT_ID]: elementId } as unknown as HTMLElement);

            if (this.unsupportedActions.has('elementIdDisplayed')) {
                return newEndpoint();
            }

            try {
                return await oldEndpoint();
            } catch (err) {
                /** jsonwire command not supported try webdriver endpoint */
                if (isOldProtocol(err)) {
                    this.unsupportedActions.add('elementIdDisplayed');
                    return newEndpoint();
                }

                throw err;
            }
        });
    }

    windowHandles() {
        return this.addToQueue(() => this.client?.getWindowHandles().then(value => ({ value })));
    }

    async url(url: string | null | undefined) {
        if (!url) {
            return undefined;
        }

        return await this.addToQueue(() => this.client?.url(encodeForSafari(url, this.isSafari(), logger)!));
    }

    reloadTab() {
        return this.addToQueue(() => this.client?.refresh());
    }

    source() { // used?
        return this.addToQueue(() => this.client?.getPageSource());
    }

    timeouts(type: keyof typeof timeoutParamIndex, ms: number) {
        return this.addToQueue(async () => {
            const oldEndpoint = () => {
                const params = [];
                params[timeoutParamIndex[type]] = ms;
                return this.client!.setTimeouts(...params);
            };

            const newEndpoint = () => this.client!.setTimeout({ [type]: ms });

            if (this.unsupportedActions.has('timeouts')) {
                return newEndpoint();
            }

            try {
                return await oldEndpoint();
            } catch (err) {
                if (isOldProtocol(err)) {
                    this.unsupportedActions.add('timeouts');
                    return newEndpoint();
                }
                throw err;
            }
        });
    }

    scroll(xoffset: number | undefined, yoffset: number | undefined) {
        xoffset = typeof xoffset === 'number' ? xoffset : 0;
        yoffset = typeof yoffset === 'number' ? yoffset : 0;

        const scroll = function (x: number, y: number) {
            window.scrollTo(x, y);
        };

        return this.execute(scroll, xoffset, yoffset);
    }

    async setValue(element: W3CElement | LegacyElement, value: string | string[]) {
        await this.elementIdClear(extractElementId(element));
        return await this.elementIdValue(extractElementId(element), value);
    }

    async getViewportSize(prop?: never): Promise<{ width: number; height: number }>;
    async getViewportSize(prop: 'width' | 'height'): Promise<number>;
    async getViewportSize(prop?: 'width' | 'height') {
        const res = await this.execute(getViewportSizeHelper);
        if (typeof prop === 'string' && /(width|height)/.test(prop)) {
            const propToGet = `screen${prop.slice(0, 1).toUpperCase()}${prop.slice(1)}` as keyof typeof res['value'];
            return res.value[propToGet];
        }
        return {
            width: res.value.screenWidth || 0,
            height: res.value.screenHeight || 0,
        };
    }

    async elementIdValue(elementId: string, value: string | string[]) {
        const stringValue = Array.isArray(value) ? value.join() : value;
        const v = await this.addToQueue(() => this.client?.elementSendKeys(elementId, stringValue));
        return ({ value: v });
    }

    elementIdClear(elementId: string) {
        return this.addToQueue(() => this.client?.elementClear(elementId));
    }

    submitFormManually(element: webdriverio.Selector) {
        return this.addToQueue(async () => {
            const form = this.client?.$(element);
            const button = form!.$('button[type="submit"]');

            if (await button.isExisting()) {
                return button.click();
            }

            const input = form!.$('input:last-of-type');
            // @ts-expect-error bad typing
            return input.keys('Enter');
        });
    }

    private async findElementAndPress(element: webdriverio.Selector, offsets: ActionOffsets, button: 'right' | 'left') {
        let value: any;
        try {
            value = await this.addToQueue(() => this.client?.$(element).click({
                x: Math.floor(offsets.clickOffset.x),
                y: Math.floor(offsets.clickOffset.y),
                button,
            }));
        } catch (err) {
            if (err.message.includes('unexpected alert open') || err.message.includes('dialog detected')) {
                await this.alertAccept().catch(innerErr => logger.warn('failed to click on alert', { err: innerErr }));
                value = await this.addToQueue(() => this.client?.$(element).click({
                    x: Math.floor(offsets.clickOffset.x),
                    y: Math.floor(offsets.clickOffset.y),
                    button,
                }));
            } else {
                throw err;
            }
        }

        return { value };
    }

    protected _rightClick(element: webdriverio.Selector, offsets: ActionOffsets) {
        return this.findElementAndPress(element, offsets, 'right');
    }

    protected _leftClick(element: webdriverio.Selector, offsets: ActionOffsets) {
        return this.findElementAndPress(element, offsets, 'left');
    }

    elementIdClick(elementId: string) {
        return this.addToQueue(() => this.client?.elementClick(elementId));
    }

    sendKeyboardShortcut(shortcutKeys: string[]) {
        return this.addToQueue(() => this.client?.keys(shortcutKeys));
    }

    async actions(actions: object[]) {
        const value = await this.addToQueue(() => this.client?.performActions(actions));
        return { value };
    }

    async doDoubleClick(element: webdriverio.Selector) {
        const value = await this.addToQueue(() => this.client?.$(element).doubleClick());
        return { value };
    }

    protected async _dragAndDrop(sourceSelector: webdriverio.Selector, destinationSelector: WebdriverIO.Element) {
        const value = await this.addToQueue(() => this.client?.$(sourceSelector).dragAndDrop(destinationSelector));
        return { value };
    }

    moveTo(element: webdriverio.Selector, xoffset?: number, yoffset?: number) {
        const data: webdriverio.MoveToOptions = {
            ...(typeof xoffset === 'number' && { xOffset: xoffset }),
            ...(typeof yoffset === 'number' && { yOffset: yoffset }),
        };

        // workaround change offset to x=1 y=1 on Safari
        // TODO: this will always be true in safari :/
        if (this.isSafari() && !Object.hasOwn(data, 'yoffset')) {
            data.yOffset = 1;
        }

        // TODO: this will always be true in safari :/
        if (this.isSafari() && !Object.hasOwn(data, 'xoffset')) {
            data.xOffset = 1;
        }

        return this.addToQueue(async () => {
            if (!this.client) {
                throw new Error('The client is not initialized');
            }

            const elem = this.client.$(element);
            await this.client.action('pointer')
                .move({ origin: elem })
                .perform();
        });
    }

    async uploadFile(localFileLocation: string) {
        const value = await this.addToQueue(() => this.client?.uploadFile(localFileLocation));
        return { value };
    }

    getUrl() {
        return this.addToQueue(() => this.client?.getUrl());
    }

    getTitle() {
        return this.addToQueue(() => this.client?.getTitle());
    }

    async windowHandleSize(size?: never): Promise<{ value: { width: number; height: number } }>;
    async windowHandleSize(size?: { width: number; height: number }): Promise<{ value: void }>;
    async windowHandleSize(size?: { width: number; height: number }): Promise<{ value: any }> {
        const value = await this.addToQueue(async () => {
            let newEndpoint: () => Promise<any> = async () => await this.client!.getWindowSize();

            if (size?.width && size?.height) {
                const width = Math.abs(size.width);
                const height = Math.abs(size.height);
                newEndpoint = async () => await this.client!.setWindowSize(width, height);
            }
            try {
                return await newEndpoint();
            } catch (err) {
                throw err;
            }
        });
        return { value };
    }

    setCookie(name: string, value: string, domain: string | undefined, httpOnly: boolean | undefined, secure: boolean | undefined, path: string | undefined, expiry: number | undefined) {
        return this.addToQueue(() => this.client?.setCookies({
            name,
            value,
            domain,
            httpOnly,
            secure,
            path,
            expiry: expiry ? Math.floor(expiry) : expiry,
        }));
    }

    getCookie(name: string | string[]) {
        return this.addToQueue(async () => {
            const res = (await this.client?.getCookies(name)) ?? [];
            return res || (typeof name === 'string' ? null : []);
        });
    }

    deleteCookie(name: string) { // used?
        return this.addToQueue(() => this.client?.deleteCookie(name));
    }

    isVisibleWithinViewport(selector: webdriverio.Selector) { // unused?
        return this.addToQueue(() => this.client?.$(selector).isDisplayed());
    }

    getCurrentTabId() {
        return this.addToQueue(() => this.client?.getWindowHandle());
    }

    frame(id: any = null) {
        return this.addToQueue(() => this.client?.switchFrame(id));
    }

    switchTab(tabId: string) {
        return this.addToQueue(() => this.client?.switchToWindow(tabId));
    }

    alertAccept() {
        return this.addToQueue(() => this.client?.acceptAlert());
    }

    log(type = 'browser') { // used?
        return this.addToQueue(() => this.client?.getLogs(type));
    }

    async end() {
        try {
            if (this.client) {
                await this.executeAsync(async (done) => {
                    await window.TSTA?.gracefulShutdown?.();
                    done(true);
                });
            }
        } catch (error) {
            logger.warn('error while trying to shutdown gracefully', { error });
        }
        this.unsupportedActions.clear();
        if (!this.queue) {
            return undefined;
        }
        return await this.addToQueue(() => this.client?.deleteSession());
    }

    async forceEnd() {
        this.unsupportedActions.clear();
        if (!this.client) {
            return undefined;
        }
        return await this.client.deleteSession();
    }

    touchAction(attr: webdriverio.TouchActions) { // unused?
        return this.addToQueue(() => this.client?.touchAction(attr));
    }

    pressKeycode(keyCode: number) {
        return this.addToQueue(() => this.client?.pressKeyCode(keyCode));
    }

    setImmediateValue(elementId: string, text: string) { // unused?
        return this.addToQueue(() => this.client?.setValueImmediate(elementId, text));
    }

    elementIdText(elementId: string) { //used?
        return this.addToQueue(() => this.client?.getElementText(elementId));
    }

    async isAppInstalled(packageName: string) { // unused?
        const isInstalled = await this.addToQueue(() => this.client?.isAppInstalled(packageName));
        logger.info(`is app (${packageName}) installed?`, { isInstalled });
        return { value: isInstalled };
    }

    launch() {
        return this.addToQueue(() => this.client?.launchApp());
    }

    context(id: string) {
        return this.addToQueue(() => this.client?.switchContext(id));
    }

    elementIdLocation(elementId: string) {
        return this.addToQueue(() => this.client?.getElementLocation(elementId));
    }
}
